import { useState } from "react";
import { 
  Sparkles, 
  Lock, 
  X, 
  Gift,
  Loader2,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAccess } from "@/contexts/AccessContext";
import { toast } from "sonner";

export default function UpgradePrompt({ onClose, showCodeRedemption = true }) {
  const { redeemCode, refreshAccess } = useAccess();
  const [inviteCode, setInviteCode] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState(false);

  const handleRedeemCode = async () => {
    if (!inviteCode.trim()) {
      toast.error("Please enter an invite code");
      return;
    }

    setIsRedeeming(true);
    try {
      const result = await redeemCode(inviteCode);
      if (result.success) {
        setRedeemSuccess(true);
        toast.success(result.message);
        setTimeout(() => {
          onClose?.();
          refreshAccess();
        }, 1500);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Failed to redeem code");
    } finally {
      setIsRedeeming(false);
    }
  };

  if (redeemSuccess) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        data-testid="upgrade-prompt"
      >
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center animate-fadeIn shadow-xl">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="text-green-500" size={32} />
          </div>
          <h3 className="text-xl font-bold text-[#1F3A5F] mb-2">
            You're all set!
          </h3>
          <p className="text-[#6B7280]">
            Enjoy unlimited access to all features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="upgrade-prompt"
    >
      <div className="bg-white rounded-2xl p-6 max-w-md w-full animate-fadeIn shadow-xl">
        {/* Close button */}
        <div className="flex justify-end mb-2">
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="close-upgrade-prompt"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-[#4FB3A5] to-[#1F3A5F] flex items-center justify-center">
          <Sparkles className="text-white" size={28} />
        </div>

        {/* Content */}
        <h3 className="text-xl font-bold text-[#1F3A5F] text-center mb-2">
          Say it better — every time
        </h3>
        <p className="text-[#6B7280] text-center mb-6 leading-relaxed">
          You've reached today's free limit. Upgrade for unlimited rewrites and advanced tones.
        </p>

        {/* Features list */}
        <div className="bg-[#F7F9FB] rounded-xl p-4 mb-6">
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-[#4FB3A5]/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle size={14} className="text-[#4FB3A5]" />
              </div>
              <span className="text-[#2C2C2C]">Unlimited message rewrites</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-[#4FB3A5]/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle size={14} className="text-[#4FB3A5]" />
              </div>
              <span className="text-[#2C2C2C]">All tone options</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-[#4FB3A5]/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle size={14} className="text-[#4FB3A5]" />
              </div>
              <span className="text-[#2C2C2C]">Faster responses</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-[#4FB3A5]/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle size={14} className="text-[#4FB3A5]" />
              </div>
              <span className="text-[#2C2C2C]">Priority features</span>
            </div>
          </div>
        </div>

        {/* Invite code section */}
        {showCodeRedemption && (
          <div className="border-t border-gray-100 pt-6">
            <div className="flex items-center gap-2 mb-3">
              <Gift size={16} className="text-[#4FB3A5]" />
              <span className="text-sm font-medium text-[#1F3A5F]">
                Have an invite code?
              </span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter code (e.g., SIB-ABC123)"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="flex-1 rounded-xl border-gray-200 focus:border-[#4FB3A5] focus:ring-[#4FB3A5]/20"
                data-testid="invite-code-input"
              />
              <Button
                onClick={handleRedeemCode}
                disabled={isRedeeming || !inviteCode.trim()}
                className="rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] px-6"
                data-testid="redeem-code-button"
              >
                {isRedeeming ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  "Redeem"
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
